import React, { FC } from 'react';

import { AllieCard, AllieLink, AllieText } from '../../../components';
import {
  ProductionOrderStatus,
  AllieProductionOrderStatusChip,
} from '../ProductionOrderStatusChip/ProductionOrderStatusChip';

import { StationSummaryStyled } from '../StationSummaryCard/StationSummaryCard.styles';
import {
  FlexContainer,
  StyledStationTitleLink,
  StyledStationButton,
  StyledOperationalStatus,
} from '../StationSummaryCardV2/StationListSummaryCard.styles';
import { OEEContainer } from '../StationSummaryCardV2/components/OEEContainer';
import { POField } from '../StationSummaryCardV2/components/POField';
import { ProgressContainer } from '../StationSummaryCardV2/components/ProgressContainer';
import { ShiftField } from '../StationSummaryCardV2/components/ShiftField';
import { Stats } from '../StationSummaryCardV2/components/Stats';
import { StationSummaryCardProps } from '../StationSummaryCardV2/StationSummaryCard.types';
import { StyledStationLink } from './SationListSummaryCardV2.styles';
import { SuspendedLine } from './components/SuspendedLine/SuspendedLine';
import { AllieLineStatusChip } from '../LineStatusChip';

const mapProductionStatusBorderColor: Record<string, string> = {
  in_progress: 'none',
  stopped: 'var(--allie-color-error-main)',
  paused: 'none',
  no_productions: 'none',
  technical_stop: 'none',
  planned_stop: 'var(--allie-color-neutral-70)',
  unplanned_stop: 'var(--allie-color-error-main)',
};

export interface StationSummaryCardPropsV2Props extends StationSummaryCardProps {
  stationLabelStatus: string;
  production: { status: string; legacyId: string; stationStatus: string };
  labels: StationSummaryCardProps['labels'] & {
    suspendedTitle?: string;
    suspendedSubtitle?: string;
    suspendedLastUpdate?: string;
  };
}

export const AllieStationListSummaryCardV2: FC<StationSummaryCardPropsV2Props> = ({
  title,
  data,
  barData,
  labels,
  onClick,
  className,
  isProductive,
  lastSectionType,
  production,
  OEE,
  statusLabel,
  shiftEndsAt,
  shiftName,
  shiftStartsAt,
  href,
  isTablet = false,
  trackingOnClickEvent,
  stationLabelStatus,
}) => {
  const isTechnicalStop =
    lastSectionType === 'neutral' && production.status === ProductionOrderStatus.STOPPED;

  if (production.stationStatus === 'SUSPENDED') {
    return (
      <StationSummaryStyled className={className}>
        <AllieCard
          style={{
            borderRadius: '4px',
            height: '181px',
            padding: isTablet ? 16 : '16px 20px',
            boxSizing: 'border-box',
            // eslint-disable-next-line max-len
            border: `2px solid ${mapProductionStatusBorderColor[production.status]}`,
          }}
          content={{
            style: { padding: 0 },
            children: (
              <FlexContainer gap={isTablet ? 16 : 24}>
                <FlexContainer flexDirection="column" gap={11} style={{ width: '100%' }}>
                  <FlexContainer>
                    <StyledStationLink
                      onClick={() =>
                        trackingOnClickEvent && trackingOnClickEvent('Station Name', title)
                      }
                      href={href}
                      isTablet={isTablet}
                      justifyContent="space-between"
                    >
                      {title}

                      <AllieLineStatusChip
                        text={stationLabelStatus}
                        lineStatus={production.stationStatus}
                      />
                    </StyledStationLink>
                  </FlexContainer>
                  <SuspendedLine
                    title={labels.suspendedTitle}
                    subtitle={labels.suspendedSubtitle}
                    lastUpdate={labels.suspendedLastUpdate}
                  />
                </FlexContainer>
              </FlexContainer>
            ),
          }}
        />
      </StationSummaryStyled>
    );
  }

  return (
    <StationSummaryStyled className={className}>
      <AllieCard
        style={{
          borderRadius: '4px',
          padding: isTablet ? 16 : '16px 20px',
          boxSizing: 'border-box',
          // eslint-disable-next-line max-len
          border: `2px solid ${mapProductionStatusBorderColor[production.status]}`,
        }}
        content={{
          style: { padding: 0 },
          children: (
            <FlexContainer gap={isTablet ? 16 : 24}>
              <FlexContainer
                flexDirection="column"
                gap={11}
                style={{ width: isTablet ? '100%' : '60%' }}
              >
                <FlexContainer
                  gap={13}
                  alignItems="center"
                  justifyContent={isTablet ? 'space-between' : ''}
                >
                  <StyledStationLink
                    onClick={() =>
                      trackingOnClickEvent && trackingOnClickEvent('Station Name', title)
                    }
                    href={href}
                    isTablet={isTablet}
                  >
                    {title}
                    {production.stationStatus !== 'PENDING_ACTIVATION' && (
                      <StyledOperationalStatus>
                        <AllieProductionOrderStatusChip
                          status={
                            isTechnicalStop
                              ? ProductionOrderStatus.TECHNICAL_STOP
                              : production.status
                          }
                          statusLabel={statusLabel}
                        />
                      </StyledOperationalStatus>
                    )}
                  </StyledStationLink>
                  <AllieLineStatusChip
                    text={stationLabelStatus}
                    lineStatus={production.stationStatus}
                  />
                </FlexContainer>
                {isTablet && (
                  <>
                    <FlexContainer justifyContent="space-between">
                      <POField label={labels.productionOrder} id={production.legacyId} />
                      <ShiftField shiftName={shiftName} />
                    </FlexContainer>

                    <Stats data={data} layout="list" isTablet={isTablet} />
                  </>
                )}
                <ProgressContainer
                  barData={barData}
                  shiftEndsAt={shiftEndsAt}
                  shiftStartsAt={shiftStartsAt}
                />
              </FlexContainer>
              {!isTablet && (
                <FlexContainer flexDirection="column" gap={16} style={{ flexGrow: 1 }}>
                  <FlexContainer alignItems="center" justifyContent="space-between">
                    <FlexContainer flexDirection="column" gap={10} style={{ flexGrow: 1 }}>
                      <FlexContainer
                        gap={10}
                        justifyContent="space-between"
                        alignItems="center"
                        style={{ height: isTablet ? '48px' : '32px', margin: '4px 2px' }}
                      >
                        <POField label={labels.productionOrder} id={production.legacyId} />
                        <ShiftField shiftName={shiftName} />
                      </FlexContainer>

                      {!isTablet && <Stats data={data} layout="list" />}
                    </FlexContainer>
                  </FlexContainer>
                </FlexContainer>
              )}
              <FlexContainer gap={24}>
                <div>
                  <FlexContainer
                    flexDirection="column"
                    alignItems="center"
                    gap={13}
                    style={{
                      width: isTablet ? '100%' : 'unset',
                      height: isTablet ? '100%' : 'unset',
                    }}
                  >
                    <StyledStationButton
                      type="button"
                      allieType="secondary"
                      allieColor="neutral"
                      fill="outline"
                      allieSize={isTablet ? 'medium' : 'xs'}
                      style={{ height: 'unset' }}
                      full
                      className="see-detail"
                      onClick={() => {
                        onClick();
                        trackingOnClickEvent &&
                          trackingOnClickEvent('View more detail button', title);
                      }}
                    >
                      {labels.button}
                    </StyledStationButton>
                    <OEEContainer value={OEE} isTablet={isTablet} />
                  </FlexContainer>
                </div>
              </FlexContainer>
            </FlexContainer>
          ),
        }}
      />
    </StationSummaryStyled>
  );
};
